import type { ApplicationListResponse } from '@acme/api/admin';
import { ApplicationService } from '@acme/api/admin';
import { ApplicationCard } from '@acme/features';
import { useCurrency } from '@acme/domains';
import {
  getCurrencySymbol,
  PaginatedList,
  TabItem,
  Tabs,
} from '@acme/foundation';
import type { ApplicationListTabConfig } from '@entities/applicationList';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type ApplicationListProps = {
  tabs: ApplicationListTabConfig[];
};

const loadingFallback = (
  <div className="rounded-lg bg-white p-6 shadow text-center text-gray-500">
    Загрузка…
  </div>
);

type ResponseData = Awaited<
  ReturnType<typeof ApplicationService.getApiAdminV1Application>
>;

function extract(response: ResponseData) {
  if (response?.isSuccess === false) {
    throw new Error(response.errorText || 'Не удалось загрузить заявки');
  }
  const items = (response?.data?.items ?? []).slice();
  items.sort((a, b) => {
    const aVal = (a as ApplicationListResponse).createAt ?? '';
    const bVal = (b as ApplicationListResponse).createAt ?? '';
    return new Date(bVal).getTime() - new Date(aVal).getTime();
  });
  return {
    items,
    totalCount: response?.data?.totalCount ?? 0,
  };
}

function toCardApplication(item: ApplicationListResponse) {
  return {
    ...item,
    createdAt: item.createAt,
    updatedAt: item.updateAt,
    epoch: item.epoch
      ? {
          id: (item.epoch as { id?: number }).id,
          name: (item.epoch as { name?: string }).name,
        }
      : null,
  };
}

export function ApplicationList({ tabs }: ApplicationListProps) {
  const navigate = useNavigate();
  const { currencies } = useCurrency();
  const [activeKey, setActiveKey] = useState(tabs[0]?.key ?? 'all');

  const handleTabChange = useCallback((key: string) => {
    setActiveKey(key);
  }, []);

  const tabItems: TabItem[] = useMemo(
    () =>
      tabs.map((tab) => ({
        key: tab.key,
        title: tab.title,
        content: (
          <PaginatedList
            key={tab.key}
            method={ApplicationService.getApiAdminV1Application}
            params={{
              epoches: tab.epochId != null ? [tab.epochId] : undefined,
            }}
            extract={extract}
            pageSize={20}
            loadingFallback={loadingFallback}
          >
            {(items) => (
              <div className="grid grid-cols-3 gap-4">
                {(items as ApplicationListResponse[]).map((card) => {
                  const currencyFromDict = currencies.find(
                    (c) => c.id === card.currency?.id
                  );
                  return (
                    <ApplicationCard
                      key={card.id}
                      application={toCardApplication(card)}
                      amount={card.currency?.amount}
                      currency={getCurrencySymbol(currencyFromDict) ?? ''}
                      onClick={() =>
                        card.id && navigate(`/applications/${card.id}`)
                      }
                    />
                  );
                })}
              </div>
            )}
          </PaginatedList>
        ),
      })),
    [tabs, navigate, currencies]
  );

  return (
    <Tabs
      items={tabItems}
      defaultActiveKey={activeKey}
      onTabChange={handleTabChange}
    />
  );
}
